import React from 'react'

import * as common from '../../../common'
import * as r from '../../../react-utils'

import * as Mui from '@mui/material'

import styles from './Input.module.css'
import { RoofClaimContext } from './RoofClaimContext'
import { roofClaimCalendarDate } from '../../routes/customer-report/roofClaimFlow'

interface RCInputProps {
  customerID: string
  disabled?: boolean
  field: keyof common.Customer
  height?: 'sm' | 'md' | 'lg'
  inputType?: React.HTMLInputTypeAttribute
  type: 'checkbox' | 'input' | 'textarea'
}

export default React.memo<RCInputProps>(function RCInput({
  customerID,
  disabled,
  field,
  height,
  inputType,
  type,
}) {
  const context = React.useContext(RoofClaimContext)
  disabled = disabled || context.locked || context.readOnly || context.isPDF
  const [fieldData, writeFieldData] = r.useCustomerField(customerID, field)
  // Jaron, Oct 30 (13433630443): what was typed when creating the customer
  // shows here until the rep types something else. Display only: nothing is
  // written on open; the first roof signature stores it (roofClaimAutofill).
  const autofill = context.autofill[field] ?? ''
  const shown =
    fieldData || (context.locked || context.cleared.has(field) ? '' : autofill)

  const handleUpdateInput = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (disabled) return
      if (autofill) context.onClearedChange(field, e.target.value === '')
      if (inputType === 'date') {
        const currentDate = Date.parse(e.target.value)
        writeFieldData(
          Number.isFinite(currentDate)
            ? common.normalizeTimestampToMs(currentDate)
            : 0,
        )
        return
      }
      // TODO: LABEL
      writeFieldData(
        inputType === 'tel'
          ? common.normalizePhoneNumber(e.target.value)
          : e.target.value,
      )
    },
    [autofill, context, disabled, field, inputType, writeFieldData],
  )
  const handleUpdateCheckbox = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) writeFieldData(e.target.checked ? 1 : 0)
    },
    [disabled, writeFieldData],
  )

  if (context.isPDF) {
    const date =
      inputType === 'date'
        ? roofClaimCalendarDate(
            common.normalizeTimestampToMs(Number(fieldData)),
          )
        : ''
    const value =
      type === 'checkbox'
        ? fieldData === 1
          ? 'Yes'
          : 'No'
        : inputType === 'date'
        ? date
        : String(shown ?? '')
    return (
      <div
        style={{
          width: type === 'textarea' ? '100%' : 203,
          whiteSpace: 'pre-wrap',
          padding: '6px 0',
        }}
      >
        <strong>{common.fieldToLabel[field] || 'Date'}: </strong>
        {value}
      </div>
    )
  }
  if (type === 'checkbox') {
    return (
      <div className={styles['checkbox-container']}>
        <span>-{common.fieldToLabel[field]}</span>
        <input
          disabled={disabled}
          checked={fieldData === 1}
          onChange={handleUpdateCheckbox}
          type="checkbox"
        />
      </div>
    )
  }

  if (type === 'input' && height === 'sm') {
    return (
      <div className={styles['input-sm']}>
        <span>{common.fieldToLabel[field]}</span>
        <input
          disabled={disabled}
          onChange={handleUpdateInput}
          type={inputType ? inputType : 'text'}
          value={String(fieldData)}
        />
      </div>
    )
  }

  if (type === 'input' && height === 'lg' && inputType !== 'date') {
    return (
      <Mui.TextField
        disabled={disabled}
        label={common.fieldToLabel[field]}
        onChange={handleUpdateInput}
        size="small"
        sx={sx['inputs']}
        type={inputType ? inputType : 'text'}
        value={shown}
      />
    )
  }

  if (type === 'input' && height === 'lg' && inputType === 'date') {
    const date = roofClaimCalendarDate(
      common.normalizeTimestampToMs(Number(fieldData)),
    )
    return (
      <Mui.TextField
        disabled={disabled}
        // label={common.fieldToLabel[field]}
        onChange={handleUpdateInput}
        size="small"
        sx={sx['inputs']}
        type={inputType ? inputType : 'text'}
        value={date}
      />
    )
  }

  if (type === 'input' && height === 'md') {
    return (
      <input
        disabled={disabled}
        onChange={handleUpdateInput}
        value={String(fieldData)}
        type="text"
      />
    )
  }

  if (type === 'textarea') {
    return (
      <Mui.TextField
        sx={sx['notes']}
        disabled={disabled}
        rows={4}
        label="Notes"
        multiline
        onChange={handleUpdateInput}
        value={String(fieldData)}
      />
    )
  }

  throw new Error('<RCInput /> -> Should be unreachable')
})

const sx = {
  inputs: {
    width: '203px',
  },
  inputInsideText: {
    height: '20px',
  },
  notes: {
    width: '100%',
  },
}
